import { router } from 'expo-router';
import { StyleSheet } from 'react-native';

import { InlineChallengeAlert } from '@fhs/loyalty/src/components';
import { useEvaluateChallenges } from '@fhs/loyalty/src/queries/loyalty.queries';
import { Button, EnhancedInlineAlert, Skeleton } from '@fhs/ui';
import { ILoyaltyQuest } from '@fhs-legacy/frontend/src/generated/graphql-gateway';
import { routes } from '@fhs-legacy/frontend/src/utils/routing';
import {
  CHALLENGE_ERROR,
  CHALLENGE_SUCCESS,
  CHALLENGE_WARNING,
  SEE_CHALLENGE_BUTTON,
} from '@fhs-legacy/frontend/src/utils/test-ids';

export function CartChallengeInlineAlert() {
  const { data, isLoading } = useEvaluateChallenges();

  if (isLoading) {
    return <Skeleton style={styles.skeleton} />;
  }

  const challengeData = data?.[0];

  if (!challengeData) {
    return null;
  }

  const alertType = getChallengeAlertType(challengeData);

  if (!alertType) {
    return null;
  }

  switch (alertType) {
    case 'error':
      return (
        <EnhancedInlineAlert
          title={challengeData.name ?? 'Challenge'}
          status="error"
          message={
            challengeData.errors?.[0]?.message ?? 'An error occurred, please try again later'
          }
          style={styles.container}
          testID={CHALLENGE_ERROR}
        />
      );

    case 'warning':
      return (
        <EnhancedInlineAlert
          title={challengeData.name ?? 'Challenge'}
          isDismissable
          status="warning"
          message={
            challengeData.warnings?.[0]?.message ??
            'A warning occurred, please check your cart items'
          }
          action={seeChallengeButton()}
          style={styles.container}
          testID={CHALLENGE_WARNING}
        />
      );

    case 'success':
      return (
        <InlineChallengeAlert
          style={styles.container}
          message={challengeData.evaluationMessage!}
          name={challengeData.name ?? ''}
          testID={CHALLENGE_SUCCESS}
        />
      );

    default:
      return null;
  }
}

const styles = StyleSheet.create({
  container: {
    marginBottom: 24,
    borderRadius: 8,
    marginHorizontal: 16,
  },
  skeleton: {
    height: 69,
    borderRadius: 8,
    marginBottom: 24,
  },
  button: {
    alignSelf: 'flex-end',
    marginTop: 4,
  },
});

type ChallengeAlertType = 'error' | 'warning' | 'success' | null;
type ChallengeData = Pick<ILoyaltyQuest, 'name' | 'errors' | 'warnings' | 'evaluationMessage'>;

function getChallengeAlertType(challengeData: ChallengeData): ChallengeAlertType {
  if (challengeData?.errors?.[0]?.message) {
    return 'error';
  }
  if (challengeData?.warnings?.[0]?.message) {
    return 'warning';
  }
  if (challengeData?.evaluationMessage) {
    return 'success';
  }
  return null;
}

function seeChallengeButton() {
  return (
    <Button
      type="unstyled"
      size="xs"
      onPress={() => router.navigate(routes.offersV2)}
      style={styles.button}
      testID={SEE_CHALLENGE_BUTTON}
    >
      <Button.Text>See Challenge</Button.Text>
    </Button>
  );
}
